import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReconnectingWebSocket from 'reconnecting-websocket';
import { SocketContext } from './SocketContext';
import { setIsSending, setIsStopable, setWebsocketError } from './Store/features/chat/chatSlice';
import * as Icons from '@phosphor-icons/react';
import { setSystemStatus, toggleDebugMode } from './Store/features/settings/settingsSlice';
import { promptService } from './Services/promptService';
import { useLocation } from 'react-router-dom';
import usePromptState from './Hooks/state/usePromptState';
import { RootState, useAppDispatch, useAppSelector } from './store';

export type IconName = keyof typeof Icons;

const SocketProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const settings = useAppSelector((state: RootState) => state.settings);
    const userSettings = useAppSelector((state: RootState) => state.userSettings);
    const [socket, setSocket] = useState<any | null>(null);
    const wsBaseUrl = settings.websocketUrl;
    const messageHandlersRef = useRef<Record<string, any>>({});
    const dispatch = useAppDispatch();
    const selectedLanguage = useAppSelector((state: RootState) => state.i18n.selectedLanguage);
    const debugMode = useAppSelector((state: RootState) => state.settings.debugMode);

    const { setPrompts } = usePromptState();

    // Keyboard shortcut für Debug Mode
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if ((event.ctrlKey || event.metaKey) && event.shiftKey && event.key.toLowerCase() === 'd') {
                event.preventDefault();
                dispatch(toggleDebugMode());
                console.log('Debug Mode toggled:', !debugMode);

                // Send debug mode state to backend via WebSocket
                if (socket) {
                    socket.send(
                        JSON.stringify({
                            type: 'setDebugMode',
                            debugMode: !debugMode,
                            language: selectedLanguage,
                        }),
                    );
                }
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [dispatch, debugMode, socket, selectedLanguage]);

    useEffect(() => {
        if (userSettings.accessToken && wsBaseUrl) {
            const options = {
                WebSocket: WebSocket,
                connectionTimeout: 2000,
                maxRetries: 20,
                maxReconnectionDelay: 15000,
                minReconnectionDelay: 500,
                reconnectionDelayGrowFactor: 1.5,
            };

            const newSocket = new ReconnectingWebSocket(wsBaseUrl, [], options);

            setSocket(newSocket);

            return () => newSocket.close();
        }
    }, [userSettings.accessToken, userSettings.userkey, wsBaseUrl]);

    useEffect(() => {
        if (socket == null) return;

        socket.onerror = (error: Event) => {
            dispatch(setWebsocketError(true));
        };

        socket.onopen = (error: Event) => {
            dispatch(setWebsocketError(false));
        };

        socket.onmessage = (event: MessageEvent) => {
            const data = JSON.parse(event.data);

            type HandlersType = {
                titleUpdate: string[];
                tokenCountUpdate: string[];
                costUpdate: string[];
                contentStream: string[];
                openaiIncidentsUpdated: string[];
                loadingAnimation: string[];
                addSourcesAndImages: string[];
                systemStatus: string[];
                updateUserPrompts: string[];
                fileDlp: string[];
                debugMessage: string[];
            };

            const handlers: HandlersType = {
                titleUpdate: ['titleUpdate', 'titleListUpdate'],
                tokenCountUpdate: ['tokenCountUpdate'],
                costUpdate: ['costUpdate'],
                contentStream: ['contentStream'],
                openaiIncidentsUpdated: ['openaiIncidentsUpdated'],
                loadingAnimation: ['loadingAnimation'],
                addSourcesAndImages: ['addSourcesAndImages'],
                systemStatus: ['systemStatus'],
                updateUserPrompts: ['updateUserPrompts'],
                fileDlp: ['fileDlp'],
                debugMessage: ['debugMessage'],
            };

            function isHandlerKey(key: string): key is keyof typeof handlers {
                return key in handlers;
            }

            const dataType = data.type as keyof typeof handlers;

            if (isHandlerKey(dataType) && handlers[dataType].length >= 1) {
                for (const handler of handlers[dataType]) {
                    const handlerFunction = messageHandlersRef.current[handler];
                    if (handlerFunction) {
                        handlerFunction(data);
                    } else {
                        console.log('Unbekannter Nachrichtentyp:', dataType);
                    }
                }
            } else {
                const handler = messageHandlersRef.current[dataType];
                if (handler) {
                    handler(data);
                }
            }
        };
    }, [socket, dispatch]);

    const addMessageHandler = useCallback((type: string, handler: (data: any) => void) => {
        messageHandlersRef.current[type] = handler;
    }, []);

    useEffect(() => {
        addMessageHandler('systemStatus', (data) => {
            if (data.direction === 'toClient') {
                dispatch(setSystemStatus(data.systemStatus));
            }
        });
    }, [addMessageHandler, dispatch]);

    useEffect(() => {
        addMessageHandler('updateUserPrompts', (data) => {
            if (data.direction === 'toClient') {
                promptService.getAllUserPromptsPaginated().then((prompts) => {
                    if (prompts !== undefined) {
                        setPrompts(prompts);
                    }
                });
            }
        });
    }, [addMessageHandler, dispatch, setPrompts]);

    useEffect(() => {
        addMessageHandler('debugMessage', (data) => {
            if (data.direction === 'toClient' && debugMode) {
                console.debug('[LLM Debug]', data.message, data.data ?? null);
            }
        });
    }, [addMessageHandler, debugMode]);

    return <SocketContext.Provider value={{ socket, addMessageHandler }}>{children}</SocketContext.Provider>;
};

export default SocketProvider;
